import clsx from "clsx";
import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/** Durée des sorties, celle de `--gx-exit` (index.css). */
export const EXIT_MS = 130;

/** Élément qui apparaît et disparaît en douceur (classes `gx-enter-*` / `gx-exit-*` de index.css) : monté dès
 *  `open`, gardé à l'écran le temps de son animation de sortie (`closing`). L'état suit `open` dès le rendu :
 *  pas d'image intermédiaire où l'élément aurait disparu avant de jouer sa sortie. */
export function usePresence(open: boolean, exitMs = EXIT_MS): { mounted: boolean; closing: boolean } {
  const [state, setState] = useState<"open" | "closing" | "closed">(open ? "open" : "closed");
  if (open && state !== "open") setState("open");
  if (!open && state === "open") setState("closing");
  useEffect(() => {
    if (state !== "closing") return;
    const timer = window.setTimeout(() => setState("closed"), exitMs);
    return () => window.clearTimeout(timer);
  }, [state, exitMs]);
  return { mounted: open || state !== "closed", closing: !open && state !== "closed" };
}

/** Dernière valeur définie : le contenu d'un élément reste lisible pendant sa sortie. */
export function useLastDefined<T>(value: T | null | undefined): T | null | undefined {
  const last = useRef(value);
  if (value !== null && value !== undefined) last.current = value;
  return value ?? last.current;
}

/** Classe d'entrée ou de sortie selon le côté d'où vient l'élément. */
export type Origin = "top" | "bottom" | "left" | "right" | "zoom" | "fade";
export function motion(origin: Origin, closing = false): string {
  return closing ? `gx-exit-${origin}` : `gx-enter-${origin}`;
}

const ClosingContext = createContext(false);

/** Entoure un élément affiché sous condition (`<Presence show={ouvert}>{ouvert && <Fenêtre />}</Presence>`) :
 *  à la fermeture, son dernier rendu reste à l'écran le temps de la sortie, et `useClosing()` le lui signale
 *  pour qu'il joue son animation de sortie. */
export function Presence({ show, children }: { show: boolean; children: ReactNode }) {
  const { mounted, closing } = usePresence(show);
  const last = useRef<ReactNode>(children);
  if (show) last.current = children;
  if (!mounted) return null;
  return <ClosingContext.Provider value={closing}>{show ? children : last.current}</ClosingContext.Provider>;
}

/** Vrai pendant l'animation de sortie d'un élément entouré de `Presence`. */
export function useClosing(): boolean {
  return useContext(ClosingContext);
}

/** Bloc (menu, liste, fenêtre) animé à l'entrée depuis `origin`, et à la sortie dans un `Presence`. */
export function Motion({
  origin,
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLDivElement> & { origin: Origin; ref?: Ref<HTMLDivElement> }) {
  const closing = useClosing();
  return <div ref={ref} {...props} className={clsx(motion(origin, closing), className)} />;
}

/** Volet latéral qui s'ouvre en largeur (éditeur de l'ordre) : la largeur s'anime, le contenu garde la sienne. */
export function Drawer({ width, className, children }: { width: number; className?: string; children: ReactNode }) {
  const closing = useClosing();
  return (
    <aside
      className={clsx("flex shrink-0 overflow-hidden", closing ? "gx-drawer-out" : "gx-drawer-in", className)}
      style={{ width }}
    >
      <div className="flex h-full shrink-0 flex-col" style={{ width }}>
        {children}
      </div>
    </aside>
  );
}

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Hauteur d'un panneau animée quand son contenu change (FLIP) : un rapport qui arrive, un formulaire qui
 *  s'allonge ou se raccourcit ne font pas sauter le panneau. L'observateur est appelé après la mise en page : la
 *  hauteur de départ est donc la dernière hauteur connue (ou, pendant une animation, la hauteur affichée), celle
 *  d'arrivée la hauteur naturelle, bornée comme d'ordinaire par le conteneur. `content` ne change de taille que
 *  par son contenu ; le panneau est observé aussi, pour tenir sa hauteur à jour hors animation (fenêtre
 *  redimensionnée). */
export function useAnimatedHeight(
  panel: RefObject<HTMLElement | null>,
  content: RefObject<HTMLElement | null>,
  enabled = true,
) {
  useLayoutEffect(() => {
    const box = panel.current;
    const inner = content.current;
    if (!enabled || !box || !inner || typeof ResizeObserver === "undefined") return;
    let last = box.getBoundingClientRect().height;
    let animation: Animation | null = null;
    const observer = new ResizeObserver((entries) => {
      if (!entries.some((entry) => entry.target === inner)) {
        if (!animation) last = box.getBoundingClientRect().height;
        return;
      }
      const from = animation ? box.getBoundingClientRect().height : last;
      animation?.cancel();
      animation = null;
      const to = box.getBoundingClientRect().height;
      last = to;
      if (Math.abs(to - from) < 1 || reducedMotion()) return;
      const running = box.animate([{ height: `${from}px` }, { height: `${to}px` }], {
        duration: 260,
        easing: "cubic-bezier(0.16, 1, 0.3, 1)",
      });
      animation = running;
      running.onfinish = () => {
        if (animation === running) animation = null;
        last = box.getBoundingClientRect().height;
      };
    });
    observer.observe(inner);
    observer.observe(box);
    return () => {
      observer.disconnect();
      animation?.cancel();
    };
  }, [panel, content, enabled]);
}
